// #2457 — Service status banner
// ----------------------------------------------------------------------------
// Polls status.2hives.ai/status.json every 60 s.
// Renders a top-of-page banner when overall status != "operational":
//   - "outage"   → red
//   - "degraded" → yellow
// Users can dismiss for 1 h via localStorage key `2hives.status.dismissed.until`.
// The banner auto-reappears on any state transition (new status key or value change).
// ----------------------------------------------------------------------------

import { useEffect, useRef, useState } from "react";
import { colors, fontSizes } from "../styles/theme";

// ---------- Types -----------------------------------------------------------

export type StatusLevel = "operational" | "degraded" | "outage" | string;

export interface StatusJson {
  overall: StatusLevel;
  message?: string;
}

// ---------- Constants -------------------------------------------------------

const STATUS_URL         = "https://status.2hives.ai/status.json";
const POLL_INTERVAL_MS   = 60_000;
const DISMISS_DURATION_MS = 60 * 60 * 1000; // 1 hour
const STORAGE_KEY_UNTIL  = "2hives.status.dismissed.until";
const STORAGE_KEY_STATE  = "2hives.status.dismissed.state";

// #2720 — skip the cross-origin poll when running on a host that can't reach
// status.2hives.ai (localhost dev, Playwright CI sandbox, *.test). Without this
// guard the browser logs "Failed to load resource: net::ERR_NAME_NOT_RESOLVED"
// to console — which the deploy smoke (smoke-config-provider) treats as a
// console-error and blocks every frontend deploy.
function isStatusPollSupported(): boolean {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  if (!host) return false;
  if (host === "localhost" || host === "127.0.0.1" || host === "0.0.0.0") return false;
  if (host.endsWith(".local") || host.endsWith(".test")) return false;
  return true;
}

// ---------- Dismiss helpers -------------------------------------------------

interface DismissRecord {
  until: number;  // epoch ms
  stateKey: string; // the state key that was dismissed
}

function getDismissRecord(): DismissRecord {
  try {
    const until = parseInt(localStorage.getItem(STORAGE_KEY_UNTIL) ?? "0", 10) || 0;
    const state = localStorage.getItem(STORAGE_KEY_STATE) ?? "";
    return { until, stateKey: state };
  } catch {
    return { until: 0, stateKey: "" };
  }
}

function setDismissRecord(key: string): void {
  try {
    localStorage.setItem(STORAGE_KEY_UNTIL, String(Date.now() + DISMISS_DURATION_MS));
    localStorage.setItem(STORAGE_KEY_STATE, key);
  } catch {
    // localStorage unavailable — ignore
  }
}

function clearDismiss(): void {
  try {
    localStorage.removeItem(STORAGE_KEY_UNTIL);
    localStorage.removeItem(STORAGE_KEY_STATE);
  } catch {
    // ignore
  }
}

/** Returns true if the current key is actively dismissed. */
function isDismissed(key: string): boolean {
  const rec = getDismissRecord();
  return rec.until > Date.now() && rec.stateKey === key;
}

// A stable key that represents "what the status currently is". If this key
// changes between polls the banner is shown again regardless of the dismiss
// timer — implementing "auto-show on state transition".
function stateKey(status: StatusJson | null): string {
  if (!status) return "";
  return `${status.overall}:${status.message ?? ""}`;
}

// ---------- Hook ------------------------------------------------------------

interface UseStatusBannerResult {
  status: StatusJson | null;
  visible: boolean;
  dismiss: () => void;
}

export function useStatusBanner(opts?: {
  fetchImpl?: typeof fetch;
  pollIntervalMs?: number;
  statusUrl?: string;
}): UseStatusBannerResult {
  const fetchImpl    = opts?.fetchImpl ?? fetch;
  const intervalMs   = opts?.pollIntervalMs ?? POLL_INTERVAL_MS;
  const url          = opts?.statusUrl ?? STATUS_URL;

  const [status,  setStatus]  = useState<StatusJson | null>(null);
  const [visible, setVisible] = useState(false);

  // Track the last state key that was *shown* to the user. When the state key
  // changes we re-evaluate visibility even if the dismiss window hasn't expired.
  const lastShownKey = useRef<string>("");

  const fetchImpl_ref = useRef(fetchImpl);
  fetchImpl_ref.current = fetchImpl;

  const applyStatus = (s: StatusJson | null) => {
    setStatus(s);

    if (!s || s.overall === "operational") {
      setVisible(false);
      return;
    }

    const key = stateKey(s);

    if (key !== lastShownKey.current) {
      // State changed (or first poll). If this exact key is currently dismissed,
      // respect it (covers reload-with-same-status scenario). Otherwise show.
      lastShownKey.current = key;
      if (isDismissed(key)) {
        setVisible(false);
      } else {
        // New / transitioned state — clear any stale dismiss + show.
        clearDismiss();
        setVisible(true);
      }
      return;
    }

    // Same state as before: re-check dismiss (timer may have expired mid-poll).
    if (isDismissed(key)) {
      setVisible(false);
    } else {
      setVisible(true);
    }
  };

  // Polling
  useEffect(() => {
    // #2720 — bail before installing the poll when the host can't reach
    // status.2hives.ai (localhost dev / Playwright CI). No fetch, no console
    // ERR_NAME_NOT_RESOLVED, no need for an environment-tolerant filter.
    // Tests that pass an explicit fetchImpl opt out of the host check.
    if (!opts?.fetchImpl && !isStatusPollSupported()) return;

    let cancelled = false;

    const poll = async () => {
      try {
        const res = await fetchImpl_ref.current(url, {
          method: "GET",
          credentials: "omit",
          headers: { Accept: "application/json" },
        });
        if (!res.ok) return; // swallow HTTP errors — keep last good state
        const data = (await res.json()) as Partial<StatusJson>;
        if (!cancelled && typeof data.overall === "string") {
          applyStatus({ overall: data.overall, message: data.message });
        }
      } catch {
        // Network error — keep last good state, don't flash banner
      }
    };

    poll();
    const id = setInterval(poll, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  // applyStatus is defined inside the component — stable via ref pattern below.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, intervalMs]);

  const dismiss = () => {
    setDismissRecord(lastShownKey.current);
    setVisible(false);
  };

  return { status, visible, dismiss };
}

// ---------- Component -------------------------------------------------------

interface StatusBannerProps {
  /** Override fetch — for tests. */
  fetchImpl?: typeof fetch;
  /** Override poll interval — for tests. */
  pollIntervalMs?: number;
  /** Override status URL — for tests. */
  statusUrl?: string;
}

export function StatusBanner({ fetchImpl, pollIntervalMs, statusUrl }: StatusBannerProps) {
  const { status, visible, dismiss } = useStatusBanner({ fetchImpl, pollIntervalMs, statusUrl });

  if (!visible || !status || status.overall === "operational") return null;

  const isOutage   = status.overall === "outage";
  const bgColor    = isOutage ? "rgba(201,64,64,0.14)" : "rgba(232,140,0,0.12)";
  const borderColor = isOutage ? colors.error : colors.warning;
  const textColor  = isOutage ? colors.error : colors.warning;
  const icon       = isOutage ? "✖" : "⚠";
  const label      = isOutage ? "Service outage" : "Degraded performance";

  return (
    <div
      data-testid="status-banner"
      data-status={status.overall}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: "0.5rem",
        background: bgColor,
        borderBottom: `1px solid ${borderColor}55`,
        padding: "0.55rem 1rem",
        fontSize: fontSizes.sm,
        color: textColor,
        // Responsive: stack on narrow viewports
        boxSizing: "border-box",
        width: "100%",
      }}
      role="alert"
      aria-live="polite"
    >
      {/* Left: icon + message */}
      <span style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
        <span aria-hidden="true" style={{ fontWeight: 700, flexShrink: 0 }}>{icon}</span>
        <strong style={{ marginRight: "0.3rem" }}>{label}.</strong>
        {status.message && (
          <span style={{ color: textColor, opacity: 0.88 }}>{status.message}</span>
        )}
      </span>

      {/* Right: "View details" + dismiss */}
      <span style={{ display: "flex", alignItems: "center", gap: "0.65rem", flexShrink: 0 }}>
        <a
          href="https://status.2hives.ai"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: textColor,
            fontSize: fontSizes.xs,
            fontWeight: 700,
            textDecoration: "underline",
            whiteSpace: "nowrap",
          }}
        >
          View details
        </a>
        <button
          onClick={dismiss}
          aria-label="Dismiss status banner"
          data-testid="status-banner-dismiss"
          style={{
            background: "transparent",
            border: `1px solid ${borderColor}66`,
            borderRadius: "4px",
            color: textColor,
            fontSize: fontSizes.xs,
            padding: "2px 8px",
            cursor: "pointer",
            fontFamily: "inherit",
            whiteSpace: "nowrap",
            lineHeight: 1.5,
          }}
        >
          Dismiss
        </button>
      </span>
    </div>
  );
}
